import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { dashboardAPI } from '../services/api';
import { fmt, statusLabel, priorityClass, statusClass } from '../utils/helpers';
import { Building2, DoorOpen, CreditCard, Wrench, TrendingUp, AlertTriangle, ArrowRight, CheckCircle, Clock, CheckCircle2, AlertCircle, CalendarClock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats]       = useState(null);
  const [revenue, setRevenue]   = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading]   = useState(true);

  const isMaintenance = user?.role === 'maintenance' || user?.role === 'contractor';
  const isTenant      = user?.role === 'tenant';
  const isFinancial   = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    const calls = isFinancial
      ? [dashboardAPI.stats(), dashboardAPI.revenue(6), dashboardAPI.activity()]
      : [dashboardAPI.stats(), Promise.resolve({ data: { data: [] } }), dashboardAPI.activity()];
    Promise.all(calls)
      .then(([s, r, a]) => {
        setStats(s.data.data);
        setRevenue(r.data.data.map(d => ({ month: d.month?.slice(5), expected: d.expected || 0, collected: d.collected || 0 })));
        setActivity(a.data.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <div className="spinner" />
    </div>
  );

  if (isMaintenance) return <MaintenanceDashboard stats={stats} activity={activity} navigate={navigate} t={t} user={user} />;
  if (isTenant)      return <TenantDashboard stats={stats} activity={activity} navigate={navigate} t={t} user={user} />;
  return <AdminDashboard stats={stats} revenue={revenue} activity={activity} navigate={navigate} t={t} user={user} />;
}

// ── Admin / Manager ────────────────────────────────────────────────────────
function AdminDashboard({ stats, revenue, activity, navigate, t, user }) {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('goodMorning') : hour < 17 ? t('goodAfternoon') : t('goodEvening');

  const pieData = [
    { name: 'Occupied',    value: stats?.occupied    || 0, color: '#059669' },
    { name: 'Vacant',      value: stats?.vacant      || 0, color: '#7c3aed' },
    { name: 'Maintenance', value: stats?.maintenance  || 0, color: '#f59e0b' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('dashboard')}</h1>
        <p className="page-subtitle">{greeting}, {user?.name?.split(' ')[0]} — {t('dashboardSubtitle')}</p>
      </div>

      {/* Rent coming due — prominent banner so it can't be missed */}
      <UpcomingRentBanner stats={stats} navigate={navigate} t={t} />

      {/* KPI Cards */}
      <div className="grid grid-4 mb-6">
        <StatCard label={t('totalProperties')} value={stats?.properties || 0} icon={<Building2 size={22}/>} color="#2563eb" sub={`${stats?.totalUnits||0} ${t('totalUnits')}`} onClick={() => navigate('/properties')} />
        <StatCard label={t('occupancyRate')} value={fmt.percent(stats?.occupancyRate)} icon={<DoorOpen size={22}/>} color="#059669" sub={`${stats?.occupied||0} ${t('occupiedUnits')} · ${stats?.vacant||0} ${t('vacantUnits')}`} onClick={() => navigate('/apartments')} />
        <StatCard label={t('monthlyRevenue')} value={fmt.currency(stats?.monthlyRevenue)} icon={<CreditCard size={22}/>} color="#d97706" sub={`${fmt.currency(stats?.monthCollected||0)} ${t('collectedThisMonth')}`} onClick={() => navigate('/payments')} />
        <StatCard label={t('openWorkOrders')} value={stats?.openWorkOrders||0} icon={<Wrench size={22}/>} color="#7c3aed" sub={`${stats?.highPriorityWO||0} ${t('highPriorityWorkOrders')}`} onClick={() => navigate('/work-orders')} />
      </div>

      {/* Charts row */}
      <div className="grid mb-6" style={{ gridTemplateColumns: '2fr 1fr', gap: 22 }}>
        <div className="card">
          <div className="card-header">
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{t('revenueAndCollections')}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-40)', marginTop: 2 }}>{t('last6Months')}</div>
            </div>
            <span className="badge badge-active"><TrendingUp size={11} /> {t('liveStatus')}</span>
          </div>
          <div className="card-body">
            {revenue.length > 0 ? (
              <ResponsiveContainer width="100%" height={210}>
                <AreaChart data={revenue} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#2563eb" stopOpacity={0.15}/>
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="gCol" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#059669" stopOpacity={0.15}/>
                      <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--ink-20)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--ink-20)' }} axisLine={false} tickLine={false} tickFormatter={v => '$' + (v/1000).toFixed(0) + 'k'} />
                  <Tooltip formatter={(v, n) => [fmt.currency(v), n === 'expected' ? t('expected') : t('collected')]} contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }} />
                  <Area type="monotone" dataKey="expected"  stroke="#2563eb" strokeWidth={2} fill="url(#gExp)" name={t('expected')} />
                  <Area type="monotone" dataKey="collected" stroke="#059669" strokeWidth={2} fill="url(#gCol)" name={t('collected')} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-20)', fontSize: 14 }}>{t('noRevenueData')}</div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><div style={{ fontWeight: 700, fontSize: 15 }}>{t('unitStatus')}</div></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={42} outerRadius={68} paddingAngle={3} dataKey="value">
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Legend iconType="circle" iconSize={9} wrapperStyle={{ fontSize: 12 }} />
                <Tooltip formatter={v => [v + ' ' + t('units')]} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {[
                { label: t('occupancyRate'),  value: fmt.percent(stats?.occupancyRate), color: '#059669' },
                { label: t('collectionRate'), value: fmt.percent(stats?.monthlyRevenue ? Math.round((stats.monthCollected / stats.monthlyRevenue) * 100) : 0), color: '#2563eb' },
              ].map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--ink-40)' }}>{r.label}</span>
                  <span style={{ fontWeight: 700, color: r.color }}>{r.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Action items + Activity */}
      <div className="grid grid-2">
        <div className="card">
          <div className="card-header">
            <div style={{ fontWeight: 700, fontSize: 15 }}>{t('actionItems')}</div>
            <span className="badge badge-overdue">{(stats?.upcomingPayments?.length || 0) + (stats?.overduePayments?.length || 0) + (stats?.expiringLeases || 0) + (stats?.highPriorityWO || 0)}</span>
          </div>
          <div>
            {(stats?.upcomingPayments || []).map(p => {
              const arrears = p.kind === 'arrears';
              const left    = p.daysUntil ?? 0;
              const urgent  = arrears || left <= 2;
              const who     = p.tenantId?.name || p.userId?.name || t('tenant');
              const sub     = arrears
                ? `${fmt.currency(p.amount)} · ${t('monthsUnpaid', { count: p.months })} · ${t('oldestDue', { date: fmt.date(p.dueDate), days: p.daysOverdue })}`
                : `${fmt.currency(p.amount)} · ${left === 0 ? t('dueToday') : t('dueInDays', { count: left })} · ${fmt.date(p.dueDate)}`;
              return (
                <ActionRow key={'rent-' + p._id}
                  icon={<CalendarClock size={15} color={urgent ? 'var(--rose)' : 'var(--amber)'} />}
                  color={urgent ? 'var(--rose)' : 'var(--amber)'}
                  label={`${arrears ? t('rentArrears') : t('rentDueSoon')} — ${who}`}
                  sub={sub}
                  onClick={() => navigate('/payments')} />
              );
            })}
            {(stats?.overduePayments || []).map(p => (
              <ActionRow key={p._id} icon={<CreditCard size={15} color="var(--rose)" />} color="var(--rose)"
                label={`${t('overdueRent')} — ${p.tenantId?.name || t('tenant')}`}
                sub={`${fmt.currency(p.amount)} · Due ${fmt.date(p.dueDate)}`}
                onClick={() => navigate('/payments')} />
            ))}
            {(stats?.expiringLeases || 0) > 0 && (
              <ActionRow icon={<Clock size={15} color="var(--amber)" />} color="var(--amber)"
                label={t('leasesExpiringLabel', { count: stats.expiringLeases })}
                sub={t('reviewAndRenew')} onClick={() => navigate('/leases')} />
            )}
            {(stats?.highPriorityWO || 0) > 0 && (
              <ActionRow icon={<Wrench size={15} color="var(--violet)" />} color="var(--violet)"
                label={`${stats.highPriorityWO} ${t('highPriorityWorkOrders')}${stats.highPriorityWO > 1 ? 's' : ''}`}
                sub={t('requiresAttention')} onClick={() => navigate('/work-orders')} />
            )}
            {(stats?.lowStockItems || 0) > 0 && (
              <ActionRow icon={<AlertTriangle size={15} color="var(--amber)" />} color="var(--amber)"
                label={t('lowStockLabel', { count: stats.lowStockItems })}
                sub={t('reorderRequired')} onClick={() => navigate('/inventory')} />
            )}
            {(!stats?.upcomingPayments?.length && !stats?.overduePayments?.length && !stats?.expiringLeases && !stats?.highPriorityWO) && (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--ink-40)' }}>
                <CheckCircle size={28} style={{ margin: '0 auto 10px', color: 'var(--emerald)', opacity: .7 }} />
                <div style={{ fontSize: 14 }}>{t('allCaughtUp')}</div>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div style={{ fontWeight: 700, fontSize: 15 }}>{t('recentActivity')}</div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/notifications')}>
              {t('viewAll')} <ArrowRight size={13} />
            </button>
          </div>
          <div>
            {activity.map((item, i) => (
              <div key={item.id} style={{ display: 'flex', gap: 12, padding: '12px 20px', borderBottom: i < activity.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, marginTop: 7, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.4 }}>{item.text}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-40)', marginTop: 2 }}>{fmt.date(item.date)}</div>
                </div>
              </div>
            ))}
            {activity.length === 0 && <div style={{ padding: '30px 20px', textAlign: 'center', color: 'var(--ink-40)', fontSize: 14 }}>{t('noRecentActivity')}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Maintenance / Contractor ───────────────────────────────────────────────
function MaintenanceDashboard({ stats, activity, navigate, t, user }) {
  const PSTATUS = {
    open:        { icon: <AlertCircle  size={13} color="var(--rose)"/>,    label: 'var(--rose)' },
    in_progress: { icon: <Clock        size={13} color="var(--brand)"/>,   label: 'var(--brand)' },
    scheduled:   { icon: <Clock        size={13} color="var(--amber)"/>,   label: 'var(--amber)' },
    completed:   { icon: <CheckCircle2 size={13} color="var(--emerald)"/>, label: 'var(--emerald)' },
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('myWorkOrders')}</h1>
        <p className="page-subtitle">{user?.name?.split(' ')[0]} — {t('maintenanceRepairTracking')}</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-4 mb-6">
        <StatCard label={t('openWorkOrders')}      value={stats?.openWorkOrders    || 0} icon={<AlertCircle  size={22}/>} color="var(--rose)"    sub={t('openWorkOrdersSub')}    onClick={() => navigate('/work-orders')} />
        <StatCard label={t('highPriorityWorkOrders')} value={stats?.highPriorityWO    || 0} icon={<AlertTriangle size={22}/>} color="var(--amber)"   sub={t('requiresAttention')}   onClick={() => navigate('/work-orders')} />
        <StatCard label={t('inProgress')}           value={stats?.inProgress        || 0} icon={<Clock         size={22}/>} color="var(--brand)"   sub=""                          onClick={() => navigate('/work-orders')} />
        <StatCard label={t('completedThisMonth')}   value={stats?.completedThisMonth|| 0} icon={<CheckCircle2  size={22}/>} color="var(--emerald)" sub=""                          onClick={() => navigate('/work-orders')} />
      </div>

      {/* Recent work orders */}
      <div className="card">
        <div className="card-header">
          <div style={{ fontWeight: 700, fontSize: 15 }}>{t('workOrderActivity')}</div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/work-orders')}>
            {t('viewAll')} <ArrowRight size={13} />
          </button>
        </div>
        <div>
          {activity.length === 0 && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ink-40)', fontSize: 14 }}>
              <CheckCircle size={28} style={{ margin: '0 auto 10px', color: 'var(--emerald)', opacity: .7 }} />
              {t('noRecentActivity')}
            </div>
          )}
          {activity.map((item, i) => {
            const ps = PSTATUS[item.status] || PSTATUS.open;
            return (
              <div key={item.id} style={{ display: 'flex', gap: 14, padding: '13px 20px', borderBottom: i < activity.length - 1 ? '1px solid var(--border-subtle)' : 'none', cursor: 'pointer' }}
                onClick={() => navigate('/work-orders')}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: ps.label + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: ps.label }}>
                  <Wrench size={16} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>{item.text}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: 'var(--ink-40)' }}>{fmt.date(item.date)}</span>
                    {item.status && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: ps.label, background: ps.label + '15', padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                        {statusLabel(item.status)}
                      </span>
                    )}
                    {item.priority === 'high' && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--rose)', background: 'var(--rose)15', padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                        {t('high')}
                      </span>
                    )}
                  </div>
                </div>
                <ArrowRight size={14} color="var(--ink-20)" style={{ alignSelf: 'center', flexShrink: 0 }} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Tenant ─────────────────────────────────────────────────────────────────
function TenantDashboard({ stats, activity, navigate, t, user }) {
  const monthlyRent   = stats?.tenantMonthlyRent ?? stats?.monthlyRevenue ?? null;
  const overdueCount  = stats?.overdueCount ?? stats?.overduePayments?.length ?? 0;
  const openWOs       = stats?.openWorkOrders ?? 0;
  const paymentActivity  = activity.filter(a => a.type === 'payment');
  const workOrderActivity = activity.filter(a => a.type === 'workorder');

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('myApartment')}</h1>
        <p className="page-subtitle">{user?.name} — {t('apartmentDetailsAndPayments')}</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-3 mb-6">
        <StatCard label={t('monthlyRent')} value={monthlyRent != null ? fmt.currency(monthlyRent) : '—'} icon={<CreditCard size={22}/>} color="#2563eb" onClick={() => navigate('/payments')} />
        <StatCard label={t('nextPaymentDue')} value={overdueCount > 0 ? `${overdueCount} ${t('overdue')}` : t('noDuePayments')} icon={<AlertTriangle size={22}/>} color={overdueCount > 0 ? '#dc2626' : '#059669'} onClick={() => navigate('/payments')} />
        <StatCard label={t('unitWorkOrders')} value={openWOs} icon={<Wrench size={22}/>} color="#7c3aed" sub={openWOs === 0 ? t('noWorkOrdersUnit') : ''} onClick={() => navigate('/work-orders')} />
      </div>

      <div className="grid grid-2">
        {/* Recent Payments */}
        <div className="card">
          <div className="card-header">
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{t('recentPayments')}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-40)', marginTop: 2 }}>{t('yourRecentPaymentHistory')}</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/payments')}>{t('viewAll')} <ArrowRight size={13}/></button>
          </div>
          <div className="card-body">
            {paymentActivity.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {paymentActivity.slice(0, 5).map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, paddingBottom: 12, borderBottom: i < Math.min(paymentActivity.length, 5) - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff' }}>
                      <CreditCard size={16} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{a.text || t('payment')}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-40)', marginTop: 1 }}>{fmt.date(a.date)}</div>
                    </div>
                    {a.amount != null && <div style={{ fontWeight: 700, fontSize: 14 }}>{fmt.currency(a.amount)}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--ink-40)', padding: 40 }}>{t('noRecentActivity')}</div>
            )}
          </div>
        </div>

        {/* Unit Work Orders */}
        <div className="card">
          <div className="card-header">
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{t('unitWorkOrders')}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-40)', marginTop: 2 }}>{t('myUnit')}</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/work-orders')}>{t('viewAll')} <ArrowRight size={13}/></button>
          </div>
          <div className="card-body">
            {workOrderActivity.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {workOrderActivity.slice(0, 5).map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, paddingBottom: 12, borderBottom: i < Math.min(workOrderActivity.length, 5) - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: '#7c3aed18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#7c3aed' }}>
                      <Wrench size={16} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{a.text}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-40)', marginTop: 1 }}>{fmt.date(a.date)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--ink-40)', padding: 40 }}>
                <CheckCircle size={28} style={{ margin: '0 auto 10px', color: 'var(--emerald)', opacity: .7 }} />
                {t('noWorkOrdersUnit')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Shared components ──────────────────────────────────────────────────────
// Whole calendar days from today until `d` (0 = due today).
function daysUntil(d) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(d);  due.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((due - today) / 86400000));
}

// Red when something is due within 2 days, amber otherwise. Rendered only when
// there is rent inside the configured notice window.
function UpcomingRentBanner({ stats, navigate, t }) {
  const list = stats?.upcomingPayments || [];
  if (!list.length) return null;

  const arrears = list.filter(p => p.kind === 'arrears');
  const owed    = arrears.reduce((s, p) => s + (p.amount || 0), 0);
  const soon    = list.filter(p => p.kind !== 'arrears');
  const soonest = soon.length ? Math.min(...soon.map(p => p.daysUntil ?? 0)) : null;
  const urgent  = arrears.length > 0 || (soonest !== null && soonest <= 2);
  const accent  = urgent ? 'var(--rose)' : 'var(--amber)';
  const tint    = urgent ? 'rgba(225,29,72,.08)' : 'rgba(245,158,11,.10)';
  const edge    = urgent ? 'rgba(225,29,72,.30)' : 'rgba(245,158,11,.35)';

  return (
    <div onClick={() => navigate('/payments')} role="button" tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') navigate('/payments'); }}
      style={{
        display:'flex', alignItems:'center', gap:13, padding:'13px 16px', marginBottom:18,
        background:tint, border:'1px solid ' + edge, borderLeft:'4px solid ' + accent,
        borderRadius:'var(--radius)', cursor:'pointer',
      }}>
      <CalendarClock size={20} color={accent} style={{ flexShrink:0 }} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:14, fontWeight:700, color:accent }}>
          {arrears.length > 0
            ? t('rentArrearsTitle', { count: arrears.length })
            : t('upcomingRentTitle', { count: soon.length })}
        </div>
        <div style={{ fontSize:12.5, color:'var(--ink-40)', marginTop:2 }}>
          {arrears.length > 0 && t('rentArrearsSub', { total: fmt.currency(owed) })}
          {arrears.length > 0 && soon.length > 0 && ' · '}
          {soon.length > 0 && t('upcomingRentSub', {
            total: fmt.currency(soon.reduce((s, p) => s + (p.amount || 0), 0)),
            days:  stats?.reminderDays ?? 5,
          })}
          {soon.length > 0 && soonest !== null && ' · '}
          {soon.length > 0 && soonest !== null &&
            (soonest === 0 ? t('dueToday') : t('soonestInDays', { count: soonest }))}
        </div>
      </div>
      <ArrowRight size={16} color={accent} style={{ flexShrink:0 }} />
    </div>
  );
}

function StatCard({ label, value, icon, color, sub, onClick }) {
  return (
    <div className="stat-card" onClick={onClick}>
      <div className="stat-accent" style={{ background: color }} />
      <div style={{ width: 44, height: 44, borderRadius: 12, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color, marginBottom: 16 }}>{icon}</div>
      <div style={{ fontSize: 27, fontWeight: 800, fontFamily: 'var(--font-display)', color: 'var(--ink)', lineHeight: 1, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-40)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--ink-20)' }}>{sub}</div>}
    </div>
  );
}

function ActionRow({ icon, label, sub, color, onClick }) {
  return (
    <div onClick={onClick} style={{ display: 'flex', gap: 12, padding: '12px 20px', cursor: 'pointer', borderBottom: '1px solid var(--border-subtle)', transition: 'background 120ms' }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
      onMouseLeave={e => e.currentTarget.style.background = ''}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-40)', marginTop: 2 }}>{sub}</div>
      </div>
      <ArrowRight size={15} color="var(--ink-20)" style={{ alignSelf: 'center', flexShrink: 0 }} />
    </div>
  );
}
